import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';

import { environment } from 'environments/environment';
import { Auditable } from 'app/models/Auditable.model';

const { apiUrl } = environment;

export type Sucursal = {
  id: number;
  activo: boolean;
  nombre: string;
  unidad: string;
  municipio: string;
  claveUnica: string;
  usuarioIds: number[];
} & Auditable;

export type NuevaSucursal = Omit<Sucursal, 'id' | 'activo' | 'claveUnica'>;

export type HorarioSucursal = {
  diaSemana: 'LUNES' | 'MARTES' | 'MIERCOLES' | 'JUEVES' | 'VIERNES' | 'SABADO' | 'DOMINGO';
  horaInicio: number;
  minutoInicio: number;
  horaFin: number;
  minutoFin: number;
};

export type DiaFestivo = {
  descripcion: string;
  fecha: string; // Formato 'DD/MM/YYYY'
  horaInicio?: number;
  minutoInicio?: number;
  horaFin?: number;
  minutoFin?: number;
};

@Injectable({
  providedIn: 'root'
})
export class SucursalService {
  private http = inject(HttpClient);
  private cambioUsuario = signal(0); // Señal para notificar cambios en el usuario, el número no tiene importancia

  public sucursalActual = signal<number | null>(null);

  get cambio(): Signal<number> {
    return this.cambioUsuario;
  }

  notificarCambioUsuario() {
    this.cambioUsuario.set(this.cambioUsuario() + 1);
  }

  getSucursales(): Observable<Sucursal[]> {
    return this.http.get<Sucursal[]>(`${apiUrl}/sucursales`);
  }

  getSucursal(id: number): Promise<Sucursal> {
    return firstValueFrom(this.http.get<Sucursal>(`${apiUrl}/sucursales/${id}/details`));
  }

  crearSucursal(sucursal: NuevaSucursal): Promise<Sucursal> {
    return firstValueFrom(this.http.post<Sucursal>(`${apiUrl}/sucursales`, sucursal));
  }

  actualizarSucursal(id: number, sucursal: Sucursal): Promise<Sucursal> {
    return firstValueFrom(this.http.put<Sucursal>(`${apiUrl}/sucursales/${id}`, sucursal));
  }

  cambiarEstatus(id: number, activo: boolean): Promise<Sucursal> {
    return firstValueFrom(this.http.put<Sucursal>(`${apiUrl}/sucursales/${id}/estatus`, { activo }));
  }

  getSucursalesDeUsuario(): Promise<Sucursal[]> {
    return firstValueFrom(this.http.get<Sucursal[]>(`${apiUrl}/sucursales/usuario`));
  }

  getHorarioSucursal(sucursalId: number): Promise<HorarioSucursal[]> {
    return firstValueFrom(this.http.get<HorarioSucursal[]>(`${apiUrl}/sucursales/${sucursalId}/horarios`));
  }

  actualizarHorarioSucursal(sucursalId: number, horarios: HorarioSucursal[]): Promise<any> {
    return firstValueFrom(this.http.put<any>(`${apiUrl}/sucursales/${sucursalId}/horarios`, horarios));
  }

  getDiasFestivosSucursal(sucursalId: number): Promise<DiaFestivo[]> {
    return firstValueFrom(this.http.get<DiaFestivo[]>(`${apiUrl}/sucursales/${sucursalId}/dias-festivos`));
  }

  actualizarDiasFestivosSucursal(sucursalId: number, diasFestivos: DiaFestivo[]): Promise<DiaFestivo[]> {
    return firstValueFrom(this.http.put<DiaFestivo[]>(`${apiUrl}/sucursales/${sucursalId}/dias-festivos`, diasFestivos));
  }

}
